'use client';
import { UserProfile } from '@/components/common/navigation/sidebar/components/user-profile';
import Button from '@/components/global/button';
import {
  Clock,
  FileSearch,
  Folder,
  HelpCircle,
  LayoutDashboard,
  Mail,
  Settings,
  ScanSearch,
} from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import React, { useMemo } from 'react';
import { featureFlagsSelectors } from '@/lib/store/feature-flags.store';
import ROUTES from '@/lib/constants/routeConfig';
import { cn } from '@/lib/utils';
import { useI18n } from '@/locales/client';
import { memo } from 'react';

interface MenuItem {
  name: string;
  route: string;
  label: string;
  icon: JSX.Element;
  children?: MenuItem[];
  enabled?: boolean;
  featureFlagKey?: string;
}

const AIToolsSidebar: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();

  const t = useI18n();
  const groupedMenu: { title?: string; items: MenuItem[] }[] = [
    {
      items: [
        {
          name: 'dashboard',
          route: ROUTES.AI_TOOLS.DASHBOARD,
          label: t('documentDashboard.title'),
          icon: <LayoutDashboard className='h-5 w-5' />,
          // enabled: true,
        },
      ],
    },
    {
      title: 'AI Tools',
      items: [
        {
          name: 'legal-forms',
          route: ROUTES.AI_TOOLS.LEGAL_FORMS.CHOICE,
          label: t('findForm.headerTitle'),
          icon: <FileSearch className='h-5 w-5' />,
          // enabled: false,
          featureFlagKey: 'LEGAL_FORMS',
        },

        {
          name: 'generate-notice',
          route: ROUTES.AI_TOOLS.FORMAL_NOTICE,
          label: t('formalNotice.title'),
          icon: <Mail className='h-5 w-5' />,
          featureFlagKey: 'FORMAL_NOTICE',
        },

        {
          name: 'caselaw',
          route: ROUTES.AI_TOOLS.CASE_LAW,
          label: t('navigation.caseLawResearch'),
          icon: <ScanSearch className='h-5 w-5' />,
          // enabled: false,
          featureFlagKey: 'CASE_LAW_RESEARCH',
        },
      ],
    },
    {
      title: 'Activity',
      items: [
        // {
        //   name: 'recent-activity',
        //   route: ROUTES.AI_TOOLS.DASHBOARD,
        //   label: t('recentActivity.title'),
        //   icon: <Clock className='h-5 w-5' />,
        //   // enabled: false,
        // },
        {
          name: 'documents',
          route: ROUTES.AI_TOOLS.MY_DOCUMENTS,
          label: t('myDocuments.title'),
          icon: <Folder className='h-5 w-5' />,
          featureFlagKey: 'FORM_LIBRARY',
          // children: [
          //   {
          //     name: 'drafts',
          //     route: ROUTES.AI_TOOLS.MY_DOCUMENTS,
          //     label: 'Drafts',
          //     icon: <PencilLine className='h-4 w-4' />,
          //   },
          //   {
          //     name: 'completed',
          //     route: ROUTES.AI_TOOLS.MY_DOCUMENTS,
          //     label: 'Completed',
          //     icon: <FileCheck className='h-4 w-4' />,
          //   },
          // ],
        },
      ],
    },
    // {
    //   title: 'Support',
    //   items: [
    //     {
    //       name: 'settings',
    //       route: ROUTES.AI_TOOLS.DASHBOARD,
    //       label: t('settings.title'),
    //       icon: <Settings className='h-5 w-5' />,
    //     },
    //     {
    //       name: 'help',
    //       route: ROUTES.AI_TOOLS.DASHBOARD,
    //       label: t('help.title'),
    //       icon: <HelpCircle className='h-5 w-5' />,
    //     },
    //   ],
    // },
  ];

  const MemoizedUserProfile = memo(UserProfile);

  const selectedTab = useMemo(() => {
    for (const section of groupedMenu) {
      for (const item of section.items) {
        if (pathname === item.route) return item.name;
        if (item.children) {
          for (const child of item.children) {
            if (pathname === child.route) return child.name;
          }
        }
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const goTo = (route: string) => router.push(route);

  return (
    <div className='border-gray-[#E5E7EB] flex h-[calc(100vh-72px)] w-full flex-col border-r bg-white dark:border-gray-700 dark:bg-gray-900'>
      <div className='flex h-full flex-col'>
        <div className='flex-1 overflow-y-auto p-6'>
          <div className='flex flex-col gap-6'>
            <div className='text-sm font-medium text-muted-foreground'>
              Menu
            </div>

            {groupedMenu.map((section, idx) => (
              <div key={idx} className='flex flex-col gap-2'>
                {section.title && (
                  <span className='text-xs font-semibold uppercase text-muted-foreground'>
                    {section.title}
                  </span>
                )}

                {section.items.map(
                  ({
                    name,
                    label,
                    route,
                    icon,
                    children,
                    featureFlagKey,
                    enabled = true,
                  }) => {
                    const isEnabled =
                      featureFlagKey != null
                        ? featureFlagsSelectors.isFeatureEnabled(featureFlagKey)
                        : enabled;

                    return (
                      <div key={route} className='flex flex-col gap-1'>
                        <Button
                          variant='ghost'
                          size='lg'
                          disabled={!isEnabled}
                          onClick={() => !children && isEnabled && goTo(route)}
                          className={cn(
                            'justify-start gap-2',
                            selectedTab === name &&
                              !children &&
                              'bg-muted font-semibold text-foreground',
                            !isEnabled && 'cursor-not-allowed opacity-50'
                          )}
                        >
                          {icon}
                          {label}
                        </Button>

                        {children && (
                          <div className='ml-6 flex flex-col gap-1'>
                            {children.map(
                              ({
                                name: childName,
                                label: childLabel,
                                icon: childIcon,
                                route: childRoute,
                                featureFlagKey: childFeatureFlagKey,
                                enabled: childEnabled = true,
                              }) => {
                                const isChildEnabled =
                                  childFeatureFlagKey != null
                                    ? featureFlagsSelectors.isFeatureEnabled(
                                        childFeatureFlagKey
                                      )
                                    : childEnabled;

                                return (
                                  <Button
                                    key={childRoute}
                                    variant='ghost'
                                    size='sm'
                                    disabled={!isChildEnabled}
                                    onClick={() =>
                                      isChildEnabled && goTo(childRoute)
                                    }
                                    className={cn(
                                      'justify-start gap-2 pl-2',
                                      selectedTab === childName &&
                                        'bg-muted font-semibold text-foreground',
                                      !isChildEnabled &&
                                        'cursor-not-allowed opacity-50'
                                    )}
                                  >
                                    {childIcon}
                                    {childLabel}
                                  </Button>
                                );
                              }
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            ))}
          </div>
        </div>

        <div className='shrink-0 border-t border-gray-200 p-6 dark:border-gray-700'>
          <MemoizedUserProfile />
        </div>
      </div>
    </div>
  );
};

export default AIToolsSidebar;
